import React, { useEffect, useState } from 'react';
import {
  Container,
  Box,
  Typography,
  Button,
  Grid,
  CircularProgress,
  Alert,
  Chip
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { fetchGymDetails, clearSelectedGym } from '../../redux/gymSlice';
import { RootState, AppDispatch } from '../../redux/store';
import { useNavigate, useParams } from 'react-router-dom';
import { GoogleMap, useLoadScript, Marker } from '@react-google-maps/api';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PeopleIcon from '@mui/icons-material/People';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const libraries: ("places")[] = ["places"];

export const GymDetailsPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { gymId } = useParams<{ gymId: string }>();
  const { selectedGym, isLoading, error } = useSelector((state: RootState) => state.gym);
  const [showMap, setShowMap] = useState(false);

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '',
    libraries
  });

  useEffect(() => {
    if (gymId) {
      dispatch(fetchGymDetails(parseInt(gymId)));
    }

    return () => {
      dispatch(clearSelectedGym());
    };
  }, [dispatch, gymId]);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
        <CircularProgress sx={{ color: '#14B8A6' }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">{error}</Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/gyms')}
          sx={{ mt: 2 }}
        >
          Back to Gyms
        </Button>
      </Container>
    );
  }

  if (!selectedGym) {
    return null;
  }

  const amenities = Array.isArray(selectedGym.amenities) 
    ? selectedGym.amenities 
    : (typeof selectedGym.amenities === 'string' ? JSON.parse(selectedGym.amenities) : []);

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 2, sm: 4 }, px: { xs: 2, sm: 3 } }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/gyms')}
          sx={{ mb: 2 }}
        >
          Back to Gyms
        </Button>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 1, fontSize: { xs: '1.75rem', sm: '2.125rem' } }}>
              {selectedGym.name}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
              <Chip
                label={selectedGym.is_active ? 'Active' : 'Inactive'}
                color={selectedGym.is_active ? 'success' : 'default'}
                size="small"
                sx={{ fontWeight: 500 }}
              />
              {selectedGym.is_verified ? (
                <Chip
                  icon={<CheckCircleIcon />}
                  label="Verified"
                  color="success"
                  size="small"
                  sx={{ fontWeight: 500 }}
                />
              ) : (
                <Chip
                  label="Pending Verification"
                  color="warning"
                  size="small"
                  sx={{ fontWeight: 500 }}
                />
              )}
            </Box>
          </Box>
          <Button
            variant="contained"
            startIcon={<EditIcon />}
            onClick={() => navigate(`/gyms/${gymId}/edit`)}
            sx={{
              height: '48px',
              minWidth: { xs: '100%', sm: '140px' },
              backgroundColor: '#14B8A6',
              '&:hover': {
                backgroundColor: '#0F9688'
              }
            }}
          >
            Edit Gym
          </Button>
        </Box>
      </Box>

      <Grid container spacing={4}>
        {/* Basic Information */}
        <Grid item xs={12} md={8}>
          <Box sx={{ mb: 4, p: 3, border: '1px solid #e0e0e0', borderRadius: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              About
            </Typography>
            <Typography variant="body1" sx={{ color: '#666', lineHeight: 1.7 }}>
              {selectedGym.description || 'No description provided'}
            </Typography>
          </Box>

          <Box sx={{ p: 3, border: '1px solid #e0e0e0', borderRadius: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
              Location
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'start', mb: 3 }}>
              <LocationOnIcon sx={{ color: '#14B8A6', mr: 1, mt: 0.5 }} />
              <Box>
                <Typography variant="body1" sx={{ fontWeight: 500, mb: 0.5 }}>
                  Address
                </Typography>
                <Typography variant="body2" sx={{ color: '#666' }}>
                  {selectedGym.address}
                </Typography>
                <Typography variant="body2" sx={{ color: '#666' }}>
                  {selectedGym.city}, {selectedGym.state} - {selectedGym.pincode}
                </Typography>
              </Box>
            </Box>

            {!showMap ? (
              <Box 
                sx={{ 
                  width: '100%', 
                  height: '300px',
                  borderRadius: 1, 
                  border: '1px solid #e0e0e0',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: '#f5f5f5',
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: '#e0e0e0'
                  }
                }}
                onClick={() => setShowMap(true)}
              >
                <Box sx={{ textAlign: 'center' }}>
                  <LocationOnIcon sx={{ fontSize: '3rem', color: '#14B8A6', mb: 1 }} />
                  <Typography variant="body1" sx={{ fontWeight: 500, color: '#666' }}>
                    Click to view map
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#999', mt: 0.5 }}>
                    {selectedGym.city}, {selectedGym.state}
                  </Typography>
                </Box>
              </Box>
            ) : (
              <Box sx={{ height: '300px', width: '100%', borderRadius: 1, overflow: 'hidden', border: '1px solid #e0e0e0' }}>
                {loadError ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                    <Typography color="error">Error loading map</Typography>
                  </Box>
                ) : !isLoaded ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                    <CircularProgress sx={{ color: '#14B8A6' }} />
                  </Box>
                ) : (
                  <GoogleMap
                    mapContainerStyle={{ width: '100%', height: '100%' }}
                    center={{ lat: Number(selectedGym.latitude), lng: Number(selectedGym.longitude) }}
                    zoom={15}
                    options={{
                      streetViewControl: false,
                      mapTypeControl: false,
                      fullscreenControl: true,
                      zoomControl: true
                    }}
                  >
                    <Marker position={{ lat: Number(selectedGym.latitude), lng: Number(selectedGym.longitude) }} />
                  </GoogleMap>
                )}
              </Box>
            )}
          </Box>
        </Grid>

        {/* Sidebar Information */}
        <Grid item xs={12} md={4}>
          <Box sx={{ mb: 4, p: 3, border: '1px solid #e0e0e0', borderRadius: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
              Contact Information
            </Typography>

            {selectedGym.phone && (
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <PhoneIcon sx={{ color: '#14B8A6', mr: 1.5, fontSize: '1.25rem' }} />
                <Box>
                  <Typography variant="body2" sx={{ color: '#999', fontSize: '0.75rem' }}>
                    Phone
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {selectedGym.phone}
                  </Typography>
                </Box>
              </Box>
            )}

            {selectedGym.email && (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <EmailIcon sx={{ color: '#14B8A6', mr: 1.5, fontSize: '1.25rem' }} />
                <Box>
                  <Typography variant="body2" sx={{ color: '#999', fontSize: '0.75rem' }}>
                    Email
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500, wordBreak: 'break-word' }}>
                    {selectedGym.email}
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>

          <Box sx={{ mb: 4, p: 3, border: '1px solid #e0e0e0', borderRadius: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
              Operating Hours
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <AccessTimeIcon sx={{ color: '#14B8A6', mr: 1.5, fontSize: '1.25rem' }} />
              <Box>
                <Typography variant="body2" sx={{ color: '#999', fontSize: '0.75rem' }}>
                  Opening Time
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {selectedGym.opening_time}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <AccessTimeIcon sx={{ color: '#14B8A6', mr: 1.5, fontSize: '1.25rem' }} />
              <Box>
                <Typography variant="body2" sx={{ color: '#999', fontSize: '0.75rem' }}>
                  Closing Time
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {selectedGym.closing_time}
                </Typography>
              </Box>
            </Box>
          </Box>

          <Box sx={{ p: 3, border: '1px solid #e0e0e0', borderRadius: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Capacity
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <PeopleIcon sx={{ color: '#14B8A6', mr: 1.5, fontSize: '1.5rem' }} />
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#14B8A6' }}>
                  {selectedGym.total_capacity}
                </Typography>
                <Typography variant="body2" sx={{ color: '#999' }}>
                  Total Members
                </Typography>
              </Box>
            </Box>
          </Box>
        </Grid>

        {/* Amenities */}
        {amenities.length > 0 && (
          <Grid item xs={12}>
            <Box sx={{ p: 3, border: '1px solid #e0e0e0', borderRadius: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Amenities
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {amenities.map((amenity: string) => (
                  <Chip
                    key={amenity}
                    label={amenity}
                    sx={{
                      backgroundColor: '#E0F2F1',
                      color: '#14B8A6',
                      fontWeight: 500
                    }}
                  />
                ))}
              </Box>
            </Box>
          </Grid>
        )}

        {/* Stats */}
        <Grid item xs={12}>
          <Box sx={{ p: 3, border: '1px solid #e0e0e0', borderRadius: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
              Performance
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={6} sm={3}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: '#14B8A6', mb: 0.5 }}>
                    {Number(selectedGym.rating || 0).toFixed(1)}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#999' }}>
                    Rating
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: '#14B8A6', mb: 0.5 }}>
                    {selectedGym.total_reviews || 0}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#999' }}>
                    Reviews
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: '#14B8A6', mb: 0.5 }}>
                    0
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#999' }}>
                    Active Slots
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: '#14B8A6', mb: 0.5 }}>
                    0
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#999' }}>
                    Bookings
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Grid>
      </Grid>
    </Container>
  );
};

export default GymDetailsPage;
